/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoChevronUpOutline, IoChevronDownOutline } from 'react-icons/io5';
import { format } from 'date-fns';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  Row,
} from '@tanstack/react-table';
import styles from './etaiements-table.module.scss';
import type { IEtaiement } from '~/types/etaiements';
import type { IOption } from '~/types/options';
import { useGetEtaiementsList } from '~/hooks/etaiements';

const columns = [{
  header: 'Nom de l\'entreprise',
  accessorKey: 'client.name',
}, {
  header: 'Agence',
  accessorKey: 'agency.name',
}, {
  header: 'Date de création',
  accessorKey: 'createdAt',
  cell: ({ getValue }: any) => {
    const value = getValue();
    if (!value) return '---';
    try {
      return format(new Date(value), 'dd.MM.yyyy');
    } catch (e) {
      return String(value);
    }
  },
}, {
  header: 'Tonnage',
  accessorKey: 'dimension.tonnage',
  cell: ({ getValue }: any) => `${Number(getValue() || 0).toFixed(2)} t`,
}, {
  header: 'Total HT',
  accessorKey: 'summary.totalHT',
  cell: ({ getValue }: any) => `${Number(getValue() || 0).toFixed(2)} €`,
}, {
  header: 'Statut',
  accessorKey: 'status',
  cell: ({ getValue, status }: any) => {
    const opt = status.find((d: IOption) => d.value === getValue());
    return (
      <>
        <div className={styles.dot} style={opt?.color ? { backgroundColor: opt.color } : {}} />
        {opt?.label || getValue()}
      </>
    );
  },
}];

const EtaiementsTable = (
  {
    etaiements,
    handleSorting,
    baseTo = '/etaiements',
  } : {
    etaiements: IEtaiement[],
    handleSorting?: any,
    baseTo?: string,
  },
) => {
  const [sorting, setSorting] = useState<any>([]);
  const navigate = useNavigate();

  const { data: list = {} } = useGetEtaiementsList();
  const { status = [] } = list;

  useEffect(() => {
    if (typeof handleSorting === 'function') handleSorting(sorting[0] || null);
  }, [sorting]);

  const data = useMemo(() => etaiements, [etaiements]);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const onRowClick = (row: Row<IEtaiement>) => {
    navigate(`${baseTo}/${row.original._id}`);
  };

  return (
    <div className={styles.etaiementsTable}>
      <table>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                >
                 <p>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  {typeof header.column.getIsSorted() === 'string' ? (
                    {
                      asc: IoChevronUpOutline({}),
                      desc: IoChevronDownOutline({}),
                    }[header.column.getIsSorted() as string]
                  ) : (
                    <span className={styles.sortIcons}>
                      {IoChevronUpOutline({})}
                      {IoChevronDownOutline({})}
                    </span>
                  )}
                 </p>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} onClick={() => onRowClick(row)}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  <div className={`${styles.cell} ${styles[cell.column.id]}`}>
                    <div className={styles.content}>
                      {cell.column.id === 'status' ? (
                        <span>
                          {flexRender(
                            cell.column.columnDef.cell,
                            {
                              ...cell.getContext(),
                              status,
                            },
                          )}
                        </span>
                      ) : flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </div>
                  </div>
              </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default EtaiementsTable;
